import { useCallback, useRef } from "react";

/**
 * Plays notification sound and optionally shows a browser/PWA push notification.
 * Works when app is installed as PWA (standalone) and in regular browser.
 */
export const useNotificationSound = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const playSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio("/notification.wav");
      }
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 0.7;
      audioRef.current.play().catch(() => {});
    } catch {
      // Silently fail
    }
  }, []);

  const showPushNotification = useCallback(
    (title: string, body?: string, link?: string) => {
      // Only show push notification if page is hidden or not focused (like WhatsApp)
      if (document.visibilityState === "visible" && document.hasFocus()) {
        // App is in foreground — just play sound, no push
        playSound();
        return;
      }

      playSound();

      if (Notification.permission !== "granted") return;

      try {
        if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.ready.then((registration) => {
            registration.showNotification(title, {
              body: body || "",
              icon: "/pwa-192.png",
              badge: "/pwa-192.png",
              tag: link || "chat-notification",
              data: { url: link || "/" },
            });
          });
        } else {
          const n = new Notification(title, {
            body: body || "",
            icon: "/pwa-192.png",
            tag: link || "chat-notification",
          });
          if (link) {
            n.onclick = () => {
              window.focus();
              window.location.href = link;
            };
          }
        }
      } catch {
        // Silently fail
      }
    },
    [playSound]
  );

  return { playSound, showPushNotification };
};
